import {
  Avatar,
  ClientSecret,
  Criterion,
  CriterionAnalysis,
  Device,
  ExtractedData,
  FSDocumentScanMode,
  FSDocumentType,
  FSFaceScanNode,
  NodeReport,
  Phrase,
  RequestedData,
  SessionReportAIAnalysis,
  SessionSettings,
  SessionStatus,
  VideoAIAnalysis,
  WebhookType
} from '@facesignai/api'

import { MaxmindCityT } from 'types/ipDetails'
import { ClientEvent } from 'types/proto'

export * from '@facesignai/api'

export enum SessionType {
  DEMO1 = 'DEMO1',
  DEMO2 = 'DEMO2',
  DEMO3 = 'DEMO3',
  FLOW = 'FLOW'
}

export interface SessionTemplate {
  id: string
  name: string
  createdAt: number
  settings: SessionSettings
}

export type MediaFile = {
  path: string
  contentType: string
}
export interface Attempt {
  clientSecret: ClientSecret
  startedAt: number
  conversation: string[]
  lastUserPhraseAppliedAt?: number
  lastAvatarPhraseAppliedAt?: number
  clientVersion?: string
  slackTs?: string
  isLocalhost?: boolean
  userVideo?: MediaFile
  avatarVideo?: MediaFile
}

export interface UserParam extends RequestedData {
  value?: string | null
}

export type PassedNode = {
  nodeId: string
  transitionId: string
}

export enum PhraseActionType {
  SHOW_EMAIL_INPUT = 'showEmailInput',
  START_DOCUMENT_SCAN = 'startDocumentScan',
  SHOW_FACE_SCAN = 'showFaceScan',
  COMPLETE = 'complete',
  SHOW_PHONE_NUMBER_INPUT = 'showPhoneNumberInput',
  REQUEST_PERMISSIONS = 'requestPermissions'
}

export type ShowEmailInputAction = {
  type: PhraseActionType.SHOW_EMAIL_INPUT
}

export type ShowSMSInputAction = {
  type: PhraseActionType.SHOW_PHONE_NUMBER_INPUT
  isoCode?: string
}

export type CompleteAction = {
  type: PhraseActionType.COMPLETE
}

export type StartDocumentScanAction = {
  type: PhraseActionType.START_DOCUMENT_SCAN
  apiKey: string
  scanningMode: FSDocumentScanMode
  allowedDocumentTypes: FSDocumentType[]
  showTorchButton?: boolean
  showCameraSwitch?: boolean
  showMirrorCameraButton?: boolean
}

export type FaceScanConfig = Pick<
  FSFaceScanNode,
  | 'captureInstructions'
  | 'requireLivenessChallenge'
  | 'enableSound'
  | 'enableHaptics'
>

export type ShowFaceScanAction = {
  type: PhraseActionType.SHOW_FACE_SCAN
  config: FaceScanConfig
}

export type RequestPermissionsAction = {
  type: PhraseActionType.REQUEST_PERMISSIONS
  camera?: boolean
  microphone?: boolean
}

export type PhraseAction =
  | ShowEmailInputAction
  | StartDocumentScanAction
  | ShowFaceScanAction
  | CompleteAction
  | ShowSMSInputAction
  | RequestPermissionsAction

export type LatencyReport = {
  nodeId: string
  processingDuration?: number
  transitionsAnalysisDuration?: number
  replyGenerationDuration?: number
  validationAIDuration?: number
  TFACodeValidationAndAnalysisDuration?: number
  TFAValidationByScript?: number
  TFAvalidationByAI?: number
  TFAReplyAnalysisDuration?: number
}

export type PhraseLatencies = {
  phraseGenerationLatency: number
  phraseGenerationModel?: string
  replyAnalysis?: number
  replyAnalysisModel?: string
  codeValidation?: number
  codeValidationModel?: string
  latencyReports: LatencyReport[]
  backendLatency?: number
  transcriptionLatency?: number
  streamingTaskLatency?: number
  startSpeakingLatency?: number
  speakingDuration?: number
  frontendReplyGenerationLatency?: number
}

export type PhraseObject = Phrase & {
  interruptedAt?: number
  latencies?: PhraseLatencies
  nodeReports?: NodeReport[]
  nodeId?: string
  action?: PhraseAction
  clientEvent?: ClientEvent
  doesNotRequireReply?: boolean
}

export type EmailVerificationAttempt = {
  ts: string
  timestamp: number
  deliveredAt?: number
}

export type SMSVerificationAttempt = {
  ts: string
  timestamp: number
  deliveredAt?: number
}

export enum EmailDeliveryStatus {
  SENDING_ERROR = 'email.sending_error',
  SENT = 'email.sent',
  DELIVERED = 'email.delivered',
  DELIVERY_DELAYED = 'email.delivery_delayed',
  COMPLAINED = 'email.complained',
  BOUNCED = 'email.bounced',
  OPENED = 'email.opened',
  CLICKED = 'email.clicked'
}

export enum SmsDeliveryStatus {
  QUEUED = 'queued',
  SENDING = 'sending',
  SENT = 'sent',
  DELIVERED = 'delivered',
  UNDELIVERED = 'undelivered',
  FAILED = 'failed'
}

export type EmailVerificationData = {
  id: string
  sessionId: string
  nodeId: string
  code: string
  email: string
  createdAt: number
  expireAt?: number
  deliveryStatus: EmailDeliveryStatus
  slackNotifications?: Record<string, EmailVerificationAttempt>
}

export type SMSVerificationData = {
  id: string
  sessionId: string
  nodeId: string
  code: string
  phoneNumber: string
  createdAt: number
  expireAt?: number
  deliveryStatus: SmsDeliveryStatus
  slackNotifications?: Record<string, SMSVerificationAttempt>
}

export type ModuleData = {
  emailVerification?: {
    verificationId?: string
    userId?: string
    emailInputSkipped?: boolean
    livenessDetected?: boolean
    userName?: string
    isVerified?: boolean
    isNotNew?: boolean
    email?: string
  }
}

export type ISessionAIAnalysis = SessionReportAIAnalysis & {
  model?: string
  latency?: number
  bestPhotoIndex?: number
  bestScreenshotId?: string
}

export type SessionLatencies = {
  heyGenInitialization?: number
  heyGenStreamingNew?: number
  heyGenConnectionPreparing?: number
  heyGenWebsocket?: number
  transcriberConnection?: number
  transcriberConnectionsAmount?: number
}

export type RecognizedUserRecord = {
  faceId: string
  userId?: string
}

export namespace DBT {
  export enum INTERRUPT {
    'SMART' = 'smart',
    'REGULAR' = 'regular',
    'DISABLED' = 'disabled'
  }

  export enum TRANSCRIBER {
    DEEPGRAM = 'deepgram',
    GLADIA = 'gladia',
    SPEECHMATICS = 'speechmatics',
    GOOGLE = 'google',
    OPENAI = 'openai'
  }

  export enum AIPlatform {
    OPENAI = 'openai',
    GROQ = 'groq',
    ANTHROPIC = 'anthropic',
    GEMINI = 'gemini',
    XAI = 'xai'
  }

  export enum ThinkingLevel {
    OFF = 'off',
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high'
  }

  export interface SubPromptT {
    prompt: string
    jsonScheme: string
    temperature?: number
    aiPlatform?: AIPlatform
    models?: Record<AIPlatform, string>
    thinkingLevel?: ThinkingLevel
  }

  export enum Demo2Stage {
    PHOTO = 'photo',
    NAME = 'name',
    REKOGNIZED_USER_GREETING = 'rekognizedUserGreeting',
    EMAIL_REQUEST = 'emailRequest',
    EMAIL_INPUT_SKIPPED = 'emailInputSkipped',
    EMAIL_SENT = 'emailSent',
    VERIFICATION = 'verification',
    VERIFICATION_SILENT = 'verificationSilent',
    USER_REPLY_ANALYSIS = 'userReplyAnalysis',
    DIGITS_EXTRACTOR = 'digitsExtractor',
    USER_NAME_EXTRACTOR = 'userNameExtractor'
  }

  export enum EMAIL_PROVIDER {
    SENDGRID = 'sendgrid',
    RESEND = 'resend'
  }

  export enum MediaResolution {
    DEFAULT = 'default',
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high'
  }

  export interface AIPromptConfT {
    prompt: string
    prompts?: Partial<Record<string, SubPromptT>>
    model?: string
    confName?: string
    temperature: number
    maxTokens?: number
    voiceId?: string
    jsonScheme?: string
    cannedResponse?: Record<string, string>
    cannedResponseTranslates?: Record<string, Record<string, string>>
    interruptionMode?: INTERRUPT
    initialPhrase?: string
    initialPhraseTranslates?: Record<string, string>
    finalPhrase?: string
    finalPhraseTranslates?: Record<string, string>
    transcriber?: TRANSCRIBER
    aiPlatform?: AIPlatform
    models?: Record<AIPlatform, string>
    avatarId?: string
    avatarQuality?: string
    directMode?: boolean
    thinkingLevel?: ThinkingLevel
    mediaResolution?: MediaResolution
    emailProvider?: EMAIL_PROVIDER
  }

  export interface GlobalSettingsT {
    domains?: string[]
  }

  export interface AIConfTemplateT {
    id: string
    name: string
    createdAt: number
    conf: AIPromptConfT
  }

  export interface AICFQT {
    prompt: string
    model: string
    temperature: number
    maxTokens: number
  }
  export interface AIConfT {
    CFQ: AICFQT
    answerAnalysis: AIPromptConfT
    responseAnalysis: AIPromptConfT
    CFQSuggestions: AICFQT
  }

  export type LivenessDetectionResult = {
    score: number
    shortDescription?: string
    longDescription?: string
    latency: number
    model?: string
  }

  export type ScreenshotT = {
    id: string
    createdAt: number
    url?: string
    file?: MediaFile
    isLast: boolean
    isFirst: boolean
    faceId?: string
    userId?: string
    livenessDetected?: boolean
    processed?: boolean
    isNew?: boolean
    rekognitionRes?: RecognizedUserRecord[]
    aiAnalysis?: ISessionAIAnalysis
    livenessRes?: LivenessDetectionResult
  }

  export type AvatarT = Avatar & {
    isDisabled?: boolean
    createdAt?: number
  }

  export type LangT = {
    id: string
    title: string
    createdAt: number
    isDisabled?: boolean
  }

  export type FaceT = {
    id: string
    createdAt: number
    // Legacy public URL (pre-migration docs). Readable for backward compat
    // until the vegas-users bucket cutover (TPS R2 §3.5).
    url?: string
    // Private object ref in the vegas-users bucket (post-migration docs).
    // Resolved to a signed URL on read via getMediaSignedUrl.
    file?: MediaFile
  }

  export type VegasUserT = Record<string, unknown> & {
    id: string
    faces: Record<string, FaceT>
    name?: string
    email?: string
    // Legacy public URL (pre-migration docs); see FaceT.url.
    photoUrl?: string
    // Private object ref for the primary photo (post-migration docs).
    photoFile?: MediaFile
    createdAt: number
    createdBySessionId?: string
    sessionsAmount?: number
    sessionsAmountInt?: number
  }

  export type TestPhrase = {
    text: string
    isAvatar: boolean
    stage?: Demo2Stage
  }

  export type Test = {
    id: string
    createdAt: number
    title: string
    metadata: Record<string, unknown>
    userInfo: Record<string, unknown>
    conversation: TestPhrase[]
    validation: string
    isPassed?: boolean
    latency?: string
    aiResponse?: Record<string, unknown>
    lastTestTimestamp?: number
    images?: string[] // list of urls
    code?: string
  }

  export type HGAvatarT = {
    id: string
    space_id: string | null
    status: string
    name: string
    preview_url: string
    is_expired: boolean
    default_voice: {
      id: string
      name: string
    }
    created_at: string
    updated_at: string
    error_message: string | null
  }

  export type VideoAIAnalysisInternal = Partial<
    Record<Criterion, CriterionAnalysis>
  >
}

export type VideoAIAnalysisDetails = {
  model: string
  jsonScheme?: string
  thinkingLevel?: string
  temperature?: number
  latency: number
}

export interface SessionObject {
  id: string
  clientId: string
  apiKeyId: string
  createdAt: number
  startedAt?: number
  finishedAt?: number
  disconnectedAt?: number
  phrases: Record<string, PhraseObject>
  conversation: string[]
  status: SessionStatus
  settings: SessionSettings
  apiVersion?: string
  usedClientSecrets: string[]
  clientSecret: ClientSecret
  attempts: Record<string, Attempt>
  slackTs?: string
  transcriptSlackTs?: string
  webhookUrl?: string
  lang?: string
  slackNotificationsDisabled?: boolean
  device?: Device
  ip?: string
  location?: MaxmindCityT
  userId?: string
  videoAIAnalysis?: VideoAIAnalysis
  videoAIAnalysisDetails?: VideoAIAnalysisDetails
  videoAnalysisErrorCode?: string
  latencies?: SessionLatencies
  sessionType: SessionType.FLOW
  user?: DBT.VegasUserT
  screenshots?: Record<string, DBT.ScreenshotT>
  referrer?: string
  extractedData?: ExtractedData
}

export type OpenAIApiRes = {
  reply: string
  data?: { key: string; value: string }[]
  flags?: { key: string; value: boolean }[]
  endSession?: boolean
  digits?: number
}

export type ApiKey = {
  id: string
  clientId: string
  key: string
  expireAt?: number
  revokedAt: number
  deletedAt: number
  createdAt: number
  name?: string
  isTest?: boolean
  domains?: string[]
}

export type WebhookConf = {
  id: string
  url: string
  createdAt: number
  events: WebhookType[]
  secret: string
}

export type Client = {
  id: string
  title: string
  createdAt: number
  deletedAt: number
  allowStandalone?: boolean
  debugInSlack?: boolean
  webhooks: Record<string, WebhookConf>
  members?: string[]
  useCase?: string
}

export type ControlT = {
  camera: boolean
  mic: boolean
}

export interface AuthUser {
  createdAt: string
  displayName?: string
  email: string
  lastLoginAt?: string
  phoneNumber?: string
  photoURL?: string
  uid: string
  [key: string]: unknown
}

export type StorageFileT = {
  url: string
  size: number
  contentType?: string
}

export enum FlowBuilderConfName {
  TRANSITIONS_ANALYSIS = 'flowBuilderTransitions',
  DIGITS_EXTRACTION = 'flowBuilderDigitsExtraction',
  USER_REPLY_ANALYSIS = 'flowBuilderUserReplyAnalysis',
  EMAIL_REQUEST = 'flowBuilderEmailRequest',
  EMAIL_VERIFICAION = 'flowBuilderEmailVerification',
  SMS_VERIFICATION = 'flowBuilderSMSVerification',
  PHONE_NUMBER_REQUEST = 'flowBuilderPhoneNumberRequest',
  CONVERSATION_NODE_WRAPPER = 'flowBuilderConversationNodeWrapper',
  VIDEO_ANALYSIS = 'flowBuilderVideoAnalysis',
  NAME_EXTRACTION = 'flowBuilderNameExtraction',
  DATA_EXTRACTION = 'flowBuilderDataExtraction'
}

export type WebhookMediaInternal = {
  id: string
  createdAt: number
  path: string
  contentType: string
}

export type WebhookInternal = {
  id: string
  clientId: string
  sessionId: string
  createdAt: number
  type: WebhookType
  url: string
  webhookId: string
  file?: WebhookMediaInternal
}

// Portal
export type PortalUser = {
  id: string
  email: string
  firstName?: string
  lastName?: string
  organizationId?: string
  createdAt: number
  updatedAt: number
}
